import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PROJECTS = [
  {
    url: "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/jws8bbyy_IMG_2079.jpg",
    title: "Toiture commerciale",
    category: "Commercial",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/5dly338x_IMG_2084.jpg",
    title: "Installation membrane",
    category: "Industriel",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/a5wx2hhs_IMG_2086.jpg",
    title: "Réfection complète",
    category: "Institutionnel",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/mwek0srs_IMG_2094.jpg",
    title: "Imperméabilisation",
    category: "Santé",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/jy6zgss6_IMG_2104.jpg",
    title: "Toiture industrielle",
    category: "Industriel",
  },
];

export default function Portfolio() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const goNext = () => setCurrentIndex((prev) => (prev + 1) % PROJECTS.length);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + PROJECTS.length) % PROJECTS.length);

  return (
    <section
      id="realisations"
      data-testid="portfolio-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <p className="tpl-overline mb-4">Nos réalisations</p>
          <h2 className="text-4xl md:text-5xl tracking-tight font-bold text-[#0F172A] max-w-2xl leading-tight">
            Des projets qui
            <span className="text-[#0077cc]"> parlent d'eux-mêmes</span>
          </h2>
        </motion.div>

        {/* Tetris Grid */}
        <div className="grid grid-cols-12 gap-2 md:gap-3">
          {/* Hero image - large */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="col-span-12 md:col-span-8 row-span-2 portfolio-item aspect-[4/3] md:aspect-auto md:min-h-[500px] cursor-pointer"
            data-testid="portfolio-item-1"
            onClick={() => openLightbox(0)}
          >
            <img src={PROJECTS[0].url} alt={PROJECTS[0].title} className="w-full h-full object-cover" loading="lazy" />
            <div className="overlay">
              <div>
                <p className="text-[#e62e37] text-xs uppercase tracking-[0.2em] font-bold mb-1">{PROJECTS[0].category}</p>
                <p className="text-white font-bold text-lg">{PROJECTS[0].title}</p>
              </div>
            </div>
          </motion.div>

          {/* Side images */}
          {[1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
              className="col-span-6 md:col-span-4 portfolio-item aspect-square cursor-pointer"
              data-testid={`portfolio-item-${i + 1}`}
              onClick={() => openLightbox(i)}
            >
              <img src={PROJECTS[i].url} alt={PROJECTS[i].title} className="w-full h-full object-cover" loading="lazy" />
              <div className="overlay">
                <div>
                  <p className="text-[#e62e37] text-xs uppercase tracking-[0.2em] font-bold mb-1">{PROJECTS[i].category}</p>
                  <p className="text-white font-bold text-base">{PROJECTS[i].title}</p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Bottom row */}
          {[3, 4].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
              className="col-span-6 portfolio-item aspect-[4/3] cursor-pointer"
              data-testid={`portfolio-item-${i + 1}`}
              onClick={() => openLightbox(i)}
            >
              <img src={PROJECTS[i].url} alt={PROJECTS[i].title} className="w-full h-full object-cover" loading="lazy" />
              <div className="overlay">
                <div>
                  <p className="text-[#e62e37] text-xs uppercase tracking-[0.2em] font-bold mb-1">{PROJECTS[i].category}</p>
                  <p className="text-white font-bold text-base">{PROJECTS[i].title}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 bg-black border-none overflow-hidden"
          data-testid="portfolio-lightbox"
        >
          <DialogTitle className="sr-only">{PROJECTS[currentIndex]?.title}</DialogTitle>
          <DialogDescription className="sr-only">Image du projet {PROJECTS[currentIndex]?.title}</DialogDescription>
          <div className="relative">
            <img
              src={PROJECTS[currentIndex]?.url}
              alt={PROJECTS[currentIndex]?.title}
              className="w-full h-auto max-h-[80vh] object-contain"
              data-testid="lightbox-image"
            />

            {/* Navigation arrows */}
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              data-testid="lightbox-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <p className="text-[#e62e37] text-xs uppercase tracking-[0.2em] font-bold mb-1">
                {PROJECTS[currentIndex]?.category}
              </p>
              <p className="text-white font-bold text-lg">{PROJECTS[currentIndex]?.title}</p>
              <p className="text-white/50 text-sm mt-1">{currentIndex + 1} / {PROJECTS.length}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
